import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import DashboardLayout from './layout/DashboardLayout';
import ResizableSlider from './common/ResizableSlider';
import {
  GasType,
  CalculationResults,
  ValidationErrors,
  CalculationMode,
  GasMixCalculatorProps,
  GasTypeUpdate
} from '../types/gasTypes';
import { 
  calculateOptimalMix, 
  validateCalculationInputs 
} from '../utils/calculations';
import { useConfigStorage, ConfigData } from '../hooks/useConfigStorage';

// Import sub-components
import ModeToggle from './GasMixCalculator/ModeToggle';
import GasTypesCarousel from './GasMixCalculator/GasTypesCarousel';
import TargetSettings from './GasMixCalculator/TargetSettings';
import CalculationResultsDisplay from './GasMixCalculator/CalculationResults';
import Button from './GasMixCalculator/common/Button';

/**
 * Gas Mix Calculator Component
 * Calculates optimal fuel mixtures for both alcohol percentage and octane rating targets
 */
const GasMixCalculator: React.FC<GasMixCalculatorProps> = ({
  initialGasTypes,
  initialBudget = 50,
  initialMode = 'alcohol'
}) => {
  const { saveConfig, loadConfig, getDefaultConfig } = useConfigStorage();
  
  // Initialize state from localStorage or defaults
  const initializeState = () => {
    const savedConfig = loadConfig();
    if (savedConfig) {
      return savedConfig;
    }
    
    // Use props if provided, otherwise use defaults
    const defaultConfig = getDefaultConfig();
    return {
      ...defaultConfig,
      gasTypes: initialGasTypes || defaultConfig.gasTypes,
      budget: initialBudget,
      calculationMode: initialMode
    };
  };

  const initialState = initializeState();
  
  // State management with proper TypeScript types
  const [gasTypes, setGasTypes] = useState<GasType[]>(initialState.gasTypes);
  const [targetAlcoholPercent, setTargetAlcoholPercent] = useState<number | string>(initialState.targetAlcoholPercent);
  const [targetOctane, setTargetOctane] = useState<number | string>(initialState.targetOctane);
  const [budget, setBudget] = useState<number | string>(initialState.budget);
  const [calculationMode, setCalculationMode] = useState<CalculationMode>(initialState.calculationMode);
  
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [error, setError] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Auto-save configuration whenever any config value changes
  useEffect(() => {
    const configData: ConfigData = {
      gasTypes,
      targetAlcoholPercent,
      targetOctane,
      budget,
      calculationMode
    };
    saveConfig(configData);
  }, [gasTypes, targetAlcoholPercent, targetOctane, budget, calculationMode, saveConfig]);

  /**
   * Adds a new gas type to the list
   */
  const addGasType = (): void => {
    const newId = Math.max(...gasTypes.map(g => g.id), 0) + 1;
    setGasTypes([...gasTypes, {
      id: newId,
      name: `Gas ${newId}`,
      alcoholPercent: 0,
      octaneRating: 87,
      pricePerLiter: 1.0
    }]);
  };

  /**
   * Removes a gas type from the list
   */
  const removeGasType = (id: number): void => {
    setGasTypes(gasTypes.filter(g => g.id !== id));
  };

  /**
   * Updates a specific field of a gas type
   */
  const updateGasType = (id: number, field: keyof GasTypeUpdate, value: string | number): void => {
    setGasTypes(gasTypes.map(g => 
      g.id === id ? { 
        ...g, 
        [field]: typeof value === 'string' 
          ? (value === '' ? '' : parseFloat(value) || 0)
          : value 
      } : g
    ));
  };

  /**
   * Validates form inputs before calculation
   */
  const validateInputs = (): boolean => {
    const validation = validateCalculationInputs(
      gasTypes,
      budget,
      targetAlcoholPercent,
      targetOctane,
      calculationMode
    );
    
    setValidationErrors(validation.errors);
    return validation.isValid;
  };

  /**
   * Main calculation function that handles both modes
   */
  const calculateMix = (): void => {
    setError('');
    setResults(null);

    if (!validateInputs()) {
      return;
    }

    try {
      const calculationResult = calculateOptimalMix(
        gasTypes,
        budget,
        targetAlcoholPercent,
        targetOctane,
        calculationMode
      );
      
      setResults(calculationResult);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Calculation failed');
    }
  };

  /**
   * Handles budget input changes with validation
   */
  const handleBudgetChange = (value: string): void => {
    setBudget(value === '' ? '' : parseFloat(value) || 0);
    
    if (validationErrors.budget) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.budget;
        return newErrors;
      });
    }
  };

  return (
    <DashboardLayout>
      {/* Mobile: Single column layout */}
      <div className="block lg:hidden">
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-4">
          {/* Calculation Mode */}
          <div className="card-professional mb-4 sm:mb-6">
            <ModeToggle
              calculationMode={calculationMode}
              onModeChange={setCalculationMode}
            />
          </div>

          {/* Target Settings */}
          <div className="card-professional mb-4 sm:mb-6">
            <TargetSettings
              calculationMode={calculationMode}
              targetAlcoholPercent={targetAlcoholPercent}
              targetOctane={targetOctane}
              budget={budget}
              validationErrors={validationErrors}
              onTargetAlcoholChange={setTargetAlcoholPercent}
              onTargetOctaneChange={setTargetOctane}
              onBudgetChange={handleBudgetChange}
            />
          </div>

          {/* Gas Types Carousel */}
          <div className="mb-4 sm:mb-6">
            <GasTypesCarousel
              gasTypes={gasTypes}
              onUpdate={updateGasType}
              onRemove={removeGasType}
              onAdd={addGasType}
            />
            
            {/* Calculate Mix Button - integrated into Gas Types section */}
            <div className="mt-4 space-y-3">
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={calculateMix}
                icon={Calculator}
                className="py-3 sm:py-4"
              >
                Calculate {calculationMode === 'alcohol' ? 'Alcohol' : 'Octane'} Mix
              </Button>

              {/* Validation Errors Display */}
              {validationErrors.gasTypes && (
                <div className="p-3 border-2 border-dashed rounded-lg" style={{ 
                  borderColor: 'var(--border-error)',
                  color: 'var(--interactive-danger)'
                }}>
                  <p className="font-medium">{validationErrors.gasTypes}</p>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="p-3 border-2 border-dashed rounded-lg" style={{ 
                  borderColor: 'var(--border-error)',
                  color: 'var(--interactive-danger)'
                }}>
                  <p className="font-medium">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Results Display - Mobile */}
          <div className="card-professional">
            {results ? (
              <CalculationResultsDisplay
                results={results}
                calculationMode={calculationMode}
                targetAlcoholPercent={typeof targetAlcoholPercent === 'string' ? parseFloat(targetAlcoholPercent) || undefined : targetAlcoholPercent}
                targetOctane={typeof targetOctane === 'string' ? parseFloat(targetOctane) || undefined : targetOctane}
              />
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Calculator className="h-12 sm:h-16 w-12 sm:w-16 mx-auto mb-4 sm:mb-6" style={{ color: 'var(--text-tertiary)' }} />
                <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Calculation Results
                </h3>
                <p className="text-base sm:text-lg font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Results will appear here
                </p>
                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  Configure your settings and click Calculate
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: ResizableSlider layout */}
      <div className="hidden lg:block -m-2 lg:-m-1">
        <ResizableSlider 
          initialLeftWidth={60}
          minLeftWidth={25}
          maxLeftWidth={75}
          className="h-full"
        >
          {/* Left Panel - All Controls */}
          <div className="dashboard-left-column h-full overflow-y-auto pr-2 lg:pr-3">
            {/* Calculation Mode */}
            <div className="card-professional mb-6">
              <ModeToggle
                calculationMode={calculationMode}
                onModeChange={setCalculationMode}
              />
            </div>

            {/* Target Settings */}
            <div className="card-professional mb-6">
              <TargetSettings
                calculationMode={calculationMode}
                targetAlcoholPercent={targetAlcoholPercent}
                targetOctane={targetOctane}
                budget={budget}
                validationErrors={validationErrors}
                onTargetAlcoholChange={setTargetAlcoholPercent}
                onTargetOctaneChange={setTargetOctane}
                onBudgetChange={handleBudgetChange}
              />
            </div>

            {/* Gas Types Carousel */}
            <div className="mb-6">
              <GasTypesCarousel
                gasTypes={gasTypes}
                onUpdate={updateGasType}
                onRemove={removeGasType}
                onAdd={addGasType}
              />
              
              {/* Calculate Mix Button - integrated into Gas Types section */}
              <div className="mt-4 space-y-3">
                <Button
                  variant="primary"
                  size="large"
                  fullWidth
                  onClick={calculateMix}
                  icon={Calculator}
                  className="py-4"
                >
                  Calculate {calculationMode === 'alcohol' ? 'Alcohol' : 'Octane'} Mix
                </Button>

                {/* Validation Errors Display */}
                {validationErrors.gasTypes && (
                  <div className="p-3 border-2 border-dashed rounded-lg" style={{ 
                    borderColor: 'var(--border-error)',
                    color: 'var(--interactive-danger)'
                  }}>
                    <p className="font-medium">{validationErrors.gasTypes}</p>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="p-3 border-2 border-dashed rounded-lg" style={{ 
                    borderColor: 'var(--border-error)',
                    color: 'var(--interactive-danger)'
                  }}>
                    <p className="font-medium">{error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Results Display */}
          <div className="dashboard-right-column h-full overflow-y-auto pl-2 lg:pl-3">
            {results ? (
              <div className="card-professional h-full">
                <CalculationResultsDisplay
                  results={results}
                  calculationMode={calculationMode}
                  targetAlcoholPercent={typeof targetAlcoholPercent === 'string' ? parseFloat(targetAlcoholPercent) || undefined : targetAlcoholPercent}
                  targetOctane={typeof targetOctane === 'string' ? parseFloat(targetOctane) || undefined : targetOctane}
                />
              </div>
            ) : (
              <div className="card-professional h-full flex items-center justify-center">
                <div className="text-center">
                  <Calculator className="h-16 w-16 mx-auto mb-6" style={{ color: 'var(--text-tertiary)' }} />
                  <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Calculation Results
                  </h3>
                  <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Results will appear here
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    Configure your settings and click Calculate
                  </p>
                </div>
              </div>
            )}
          </div>
        </ResizableSlider>
      </div>
    </DashboardLayout>
  );
};

export default GasMixCalculator;